import { Products } from './products';
import { Options, Problems, Rooms } from './wizard';

type QuestionResult<TOption extends Options> = Record<TOption, boolean>;

const buildResultObject = <
  TOption extends Options,
  TEnum extends Record<string, TOption>
>(
  possibleValues: TEnum,
  selectedValues: TOption[]
): QuestionResult<TOption> =>
  Object.fromEntries(
    Object.values(possibleValues).map(value => [
      value,
      selectedValues.includes(value),
    ])
  ) as QuestionResult<TOption>;

// Those combinations require the ViewPlus (PM Sensor or Air quality + Radon)
const recommendedProductForPMSensor = (problems: QuestionResult<Problems>) => {
  if (
    problems[Problems.Allergies] ||
    problems[Problems.Asthma] ||
    (problems[Problems.AirQuality] && problems[Problems.Radon])
  ) {
    return Products.ViewPlus;
  }
};

const recommendedProductForAirQuality = (rooms: QuestionResult<Rooms>) => {
  if (!rooms[Rooms.LivingRoom] && !rooms[Rooms.Kitchen])
    return Products.WaveMini;

  return Products.ViewPlus;
};

const recommendedProductForRadon = (rooms: QuestionResult<Rooms>) => {
  if (
    !rooms[Rooms.Bedroom] &&
    !rooms[Rooms.LivingRoom] &&
    !rooms[Rooms.Kitchen]
  )
    return Products.ViewRadon;

  return Products.ViewPlus;
};

const recommendedProductWithoutSpecificProblem = (
  rooms: QuestionResult<Rooms>
) => {
  if (
    rooms[Rooms.LivingRoom] ||
    rooms[Rooms.Bedroom] ||
    rooms[Rooms.Kitchen] ||
    (rooms[Rooms.Basement] && rooms[Rooms.Other])
  )
    return Products.ViewPlus;

  if (rooms[Rooms.Basement]) return Products.ViewRadon;
  if (rooms[Rooms.Other]) return Products.WaveMini;
};

export const computeRecommendedProduct = (
  selectedProblems: Problems[],
  selectedRooms: Rooms[]
): Products => {
  const problems = buildResultObject(Problems, selectedProblems);
  const rooms = buildResultObject(Rooms, selectedRooms);

  return (
    recommendedProductForPMSensor(problems) ||
    (selectedProblems.length === 0 &&
      recommendedProductWithoutSpecificProblem(rooms)) ||
    (problems.airQuality && recommendedProductForAirQuality(rooms)) ||
    (problems.radon && recommendedProductForRadon(rooms)) ||
    // Fallback to ViewPlus
    Products.ViewPlus
  );
};
